export interface RevealOptions {
    /** Breathing room kept between the target and the view's edge, in px. */
    margin?: number;
    /**
     * An element above the target to keep fully in view while that still shows the target (the X3-B2 line): the scroll
     * stops at its top when stopping there shows the whole target, even if with less than `margin` below it. When it
     * cannot stay, it is scrolled fully out rather than left cut in half at the edge (if the target's top allows).
     */
    keep?: HTMLElement | null;
    /**
     * UX3-04: an element pinned (`position: sticky; top: 0`) to the top of the scrolling ancestor -- the X3-B2 line. It
     * covers the top of the view once the body scrolls, so the target counts as visible only below it, and is revealed
     * there. Ignored when it is outside that ancestor or has no height. A `keep` element whose computed position is
     * `sticky` is treated as this (the modal passes the X3-B2 line as `keep`; the stylesheet pins it).
     */
    stickyTop?: HTMLElement | null;
}

/**
 * UX1-07 (FIX-3b): bring `target` into view inside its nearest scrolling ancestor (the modal body), moving as little as
 * possible -- nothing when it is already fully visible, otherwise just enough to show its bottom edge, but never so far
 * that its top leaves the view (a target taller than the view is aligned to its top). Only that ancestor scrolls, never
 * the page behind a fixed modal (which `scrollIntoView` would also move). Smooth unless the user prefers reduced motion.
 *
 * Returns the new `scrollTop`, or `null` when nothing moved.
 */
export function revealInScrollParent(target: HTMLElement | null | undefined, opts: RevealOptions = {}): number | null {
    if (!target) return null;
    const margin = opts.margin ?? 12;
    const parent = scrollParentOf(target);
    if (!parent) return null;
    const view = parent.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    const pinned = opts.stickyTop ?? (isSticky(opts.keep) ? opts.keep : null);
    const stuck = pinned && pinned !== target && parent.contains(pinned) ? pinned.getBoundingClientRect() : null;
    const inset = stuck ? Math.max(0, (stuck.height ?? stuck.bottom - stuck.top) || 0) : 0;
    const fromTop = box.top - view.top - inset;
    const pastBottom = box.bottom - view.bottom;
    let delta = 0;
    if (fromTop < margin) {
        delta = fromTop - margin;
    } else if (pastBottom > -margin) {
        let want = pastBottom + margin;
        const most = fromTop - margin;
        if (!stuck && opts.keep && parent.contains(opts.keep)) {
            // Scrolling past this clips the kept element's top; honour it while the target still fits completely.
            const kept = opts.keep.getBoundingClientRect();
            const keepLimit = kept.top - view.top;
            const keepGone = kept.bottom - view.top;
            if (keepLimit >= pastBottom) {
                want = Math.min(want, keepLimit);
            } else if (keepLimit < want && keepGone > want && keepGone <= most) {
                want = keepGone;
            }
        }
        delta = Math.max(0, Math.min(want, most));
    }
    delta = Math.round(delta);
    if (delta === 0) return null;
    const top = Math.max(0, parent.scrollTop + delta);
    if (typeof parent.scrollTo === 'function') {
        parent.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } else {
        parent.scrollTop = top;
    }
    return top;
}

function isSticky(el: HTMLElement | null | undefined): boolean {
    if (!el || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return false;
    try {
        return window.getComputedStyle(el).position === 'sticky';
    } catch {
        return false;
    }
}

function scrollParentOf(el: HTMLElement): HTMLElement | null {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return null;
    for (let node = el.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    }
    return null;
}

function prefersReducedMotion(): boolean {
    try {
        return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
        return false;
    }
}
